import * as React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, ModalProps, Paper, Stack, Tooltip, Typography } from '@mui/material';
import MuiIconButton from '@mui/material/IconButton';
import { styled } from '@mui/system';

import dayjs from 'dayjs';
import { useModal } from 'mui-modal-provider';

import { environment } from '../../../../config';
import { VenueReservationContentItemV2 } from '../../../../graphql/API';
import { useAppSelector } from '../../../../redux/hooks';
import Icons from '../../../../themes/icons';
import { centsToDollars } from '../../../../utils/formatter';
import { VENUE_MANUAL_TAGS } from '../../../../utils/venue-tags';
import UserAvatarCategory from '../../../Avatar/UserAvatarCategory/UserAvatarCategory';
import Button from '../../../Button/Button';
import SaveUserButton from '../../../Button/SaveUserButton';
import { FeedIcon, ReelIcon, StoryIcon, TikTokIcon } from '../../../Icons';
import LinkProfile from '../../../../pages/Collabs/components/LinkProfile';
import BuyContentDialog from '../../BuyContent/BuyContentDialog';
import { ConfirmAction } from '../../Confirm/context/types';
import UsageRightsDialog from '../../UsageRights/UsageRightsDialog';
import { ActionBuyDownload } from './ActionBuyDownload';
import { ActionBuyDownloadBeforeUpdate } from './ActionBuyDownloadBeforeUpdate';
import { BuyContent } from './BuyContent';
import MediaContent from './MediaContent';
import { NoPriceBuyContent } from './NoPriceBuyContent';
import useAction from './useAction';

const baseUrl = (() => {
  const envUrl = environment === 'production' ? '' : `-${environment}`;
  return `https://assets${envUrl}.beautypass.app`;
})();

const IconButton = styled(MuiIconButton)({
  backgroundColor: 'white',
  ':hover': {
    backgroundColor: 'rgb(255,255,255,0.84)',
  },
});

export type ContentProps = {
  itemId: number;
  items: VenueReservationContentItemV2[];
  onClose?: ModalProps['onClose'];
  confirm: ConfirmAction;
};

export const BUY_CONTENT_COST = 5.0;

const Content: React.FC<ContentProps> = ({ onClose, ...restProps }) => {
  const { balance, status } = useAppSelector((state) => state.balance.value) || {};
  const { tags: venueTags } = useAppSelector((state) => state.venue.value) || {};

  const availableBalance = Number(centsToDollars(balance || 0));

  const [showSavedTooltip, setShowSavedTooltip] = React.useState(false);

  const { showModal } = useModal();
  const {
    item,
    handleDownloadContent,
    handleInviteUser,
    handleBuyContent,
    handleSaveUser,
    handleNavigation,
    getLoading,
  } = useAction(restProps);

  const handleSaveUserWithTooltip = React.useCallback(
    (type: 'saved' | 'unsaved') => {
      handleSaveUser();
      if (type === 'saved') {
        setShowSavedTooltip(true);
        setTimeout(() => {
          setShowSavedTooltip(false);
        }, 2000);
      }
    },
    [handleSaveUser],
  );

  React.useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose && onClose({}, 'escapeKeyDown');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyPress);

    return () => {
      window.removeEventListener('keydown', onKeyPress);
    };
  }, [onClose]);

  const isSaveLoading = React.useMemo<boolean | undefined>(
    () => getLoading('saveContent', String(item?.id)),
    [getLoading, item?.id],
  );

  const handleUsageRightsDialog = () => {
    showModal(UsageRightsDialog, {});
  };

  const handleBuyContentDialog = () => {
    showModal(BuyContentDialog, {
      balance: availableBalance,
      onConfirm: handleBuyContent,
      cost: item?.price || 0,
    });
  };

  const postedDate = item?.createdAt
    ? new Date(item.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  const postTypeLabel =
    item?.postOnInstagramStories || item?.postOnInstagramStories3x || item?.postOnInstagramStories2x
      ? 'Story'
      : item?.postOnInstagramFeed
        ? 'Feed'
        : item?.postOnInstagramReels
          ? 'Reel'
          : item?.postOnTikTokVideo
            ? 'TikTok'
            : null;

  const PostTypeIcon =
    item?.postOnInstagramStories || item?.postOnInstagramStories3x || item?.postOnInstagramStories2x
      ? StoryIcon
      : item?.postOnInstagramFeed
        ? FeedIcon
        : item?.postOnInstagramReels
          ? ReelIcon
          : item?.postOnTikTokVideo
            ? TikTokIcon
            : null;

  const isVideo = Boolean(item?.images?.video);

  return (
    <Stack direction='row' spacing={1} alignItems='center'>
      <IconButton color='primary' onClick={() => handleNavigation('prev')} sx={{ flexShrink: 0 }}>
        <ArrowBackIcon />
      </IconButton>
      <Paper
        sx={{
          position: 'relative',
          minWidth: '600px',
          maxWidth: '30vw',
          borderRadius: 2,
          overflow: 'auto',
          maxHeight: '90vh',
        }}
      >
        <Stack direction='column'>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 2,
              borderBottom: '1px solid #EFEEEE',
            }}
          >
            <UserAvatarCategory
              src={item?.user?.picture?.thumbnail ?? ''}
              sx={{ height: 48, width: 48, flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <LinkProfile
                url={item?.user?.id != null ? `/users/${item.user.id}` : '#'}
                label={`${item?.user?.firstname ?? ''} ${item?.user?.lastname ?? ''}`.trim() || '—'}
                fontSize='16px'
                fontWeight='600'
                hoverTextDecoration='underline'
              />
              <Typography variant='body2' color='text.secondary' noWrap sx={{ mt: 0.25 }}>
                {item?.offerTitle ?? '—'}
              </Typography>
            </Box>
            <Tooltip
              open={showSavedTooltip}
              title={
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111' }}>
                    Profile saved
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#5F5F60' }}>
                    You can find it in Paid Collabs
                  </Typography>
                </Box>
              }
              placement='bottom'
              arrow
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -4],
                      },
                    },
                  ],
                },
                tooltip: {
                  sx: {
                    bgcolor: 'white',
                    color: '#111',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
                    borderRadius: '8px',
                    p: 1.5,
                    '& .MuiTooltip-arrow': {
                      color: 'white',
                    },
                  },
                },
              }}
            >
              <Box sx={{ flexShrink: 0 }}>
                <SaveUserButton
                  size='small'
                  userId={Number(item?.user?.id)}
                  saved={Boolean(item?.user?.savedByVenue)}
                  onSuccess={handleSaveUserWithTooltip}
                  isOnlineBrand
                />
              </Box>
            </Tooltip>
          </Box>

          {/* Media */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1',
              maxHeight: '55vh',
              bgcolor: '#F0F0F0',
              overflow: 'hidden',
            }}
          >
            <MediaContent
              src={item?.images?.medium ?? ''}
              videoSrc={
                item?.images?.video
                  ? `${baseUrl}/${item.content}`
                  : item?.images?.video || undefined
              }
              showDownloadOption={false}
              autoPlay={isVideo}
            />
            {!item?.ispost && !item?.byVenue &&
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    opacity: 0.92,
                  }}
                >
                  <Icons.BeautyPassWhiteIcon
                    style={{
                      width: 40,
                      height: 40,
                      filter: 'brightness(0) invert(1)',
                    }}
                  />
                  <Typography
                    component='span'
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 600,
                      fontSize: '40px',
                      lineHeight: 1,
                      letterSpacing: 0,
                      color: '#FFFFFF',
                    }}
                  >
                    Beautypass
                  </Typography>
                </Box>
              </Box>
            }
          </Box>

          {/* Details: Post view = Post / Posted date / Post media; Buy content view = Upload date / Price / Usage rights */}
          <Box sx={{ display: 'flex', borderBottom: '1px solid #EFEEEE' }}>
            {item?.ispost ? (
              <>
                <Box sx={{ flex: 1, px: 3, py: 2.5, borderRight: '1px solid #EFEEEE', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography color='#5F5F60' fontSize='14px' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Post
                  </Typography>
                  {postTypeLabel && PostTypeIcon ? (
                    <Box display='flex' alignItems='center' gap={1}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 0.5 }}>
                        <PostTypeIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Typography fontSize='14px' fontWeight={500} color='#5F5F60' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {postTypeLabel}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography fontSize='14px' fontWeight={500} color='#5F5F60'>—</Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1, px: 3, py: 2.5, borderRight: '1px solid #EFEEEE', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography color='#5F5F60' fontSize='14px' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Posted date
                  </Typography>
                  <Typography fontSize='14px' fontWeight={500} color='#111' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    {postedDate || '—'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography color='#5F5F60' fontSize='14px' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Post media
                  </Typography>
                  <Typography fontSize='14px' fontWeight={500} color='#111' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    {isVideo ? 'Video' : 'Image'}
                  </Typography>
                </Box>
              </>
            ) : item?.byVenue ? (
              <>
                <Box sx={{ flex: 1, px: 3, py: 2.5, borderRight: '1px solid #EFEEEE', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography color='#5F5F60' fontSize='14px' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Purchase date
                  </Typography>
                  <Typography fontSize='14px' fontWeight={500} color='#111' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    {item?.downloadedAt ? dayjs(item.downloadedAt).format('MMMM D, YYYY') : '—'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, px: 3, py: 2.5, borderRight: '1px solid #EFEEEE', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography color='#5F5F60' fontSize='14px' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Usage rights
                  </Typography>
                  <Box display='flex' alignItems='center' gap={0.5}>
                    <Icons.UsageBadgeIcon style={{ width: 18, height: 18 }} />
                    <Typography
                      component='button'
                      onClick={handleUsageRightsDialog}
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#111',
                        fontFamily: 'Poppins, sans-serif',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                      }}
                    >
                      Commercial
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1, px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography color='#5F5F60' fontSize='14px' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Expiration
                  </Typography>
                  <Typography fontSize='14px' fontWeight={500} color='#111' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    {(() => {
                      if (!item?.downloadedAt) return '—';
                      const purchaseDay = dayjs(item.downloadedAt);
                      const expirationDay = purchaseDay.add(90, 'day');
                      const remaining = expirationDay.diff(dayjs(), 'day');
                      return `${Math.max(0, remaining)} days left`;
                    })()}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ flex: 1, px: 3, py: 2.5, borderRight: '1px solid #EFEEEE', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography color='#5F5F60' fontSize='14px' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Upload date
                  </Typography>
                  <Typography fontSize='14px' fontWeight={500} color='#111' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    {postedDate || '—'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, px: 3, py: 2.5, borderRight: '1px solid #EFEEEE', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography color='#5F5F60' fontSize='14px' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Price
                  </Typography>
                  <Typography fontSize='14px' fontWeight={500} color='#111' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    $ {(item?.price ?? 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography color='#5F5F60' fontSize='14px' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Usage rights
                  </Typography>
                  <Typography fontSize='14px' fontWeight={500} color='#111' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Unlock on purchase
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ p: 3 }}>
            {item?.ispost ? (
              item?.postLink ? (
                <Button
                  fullWidth
                  variant='contained'
                  component='a'
                  href={item.postLink}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{
                    py: 1.5,
                    backgroundColor: '#000',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#333' },
                  }}
                >
                  Go to post
                </Button>
              ) : null
            ) : (
              <>
                {isSaveLoading === false || item?.byVenue ? (
                  <>
                    {!item?.price ? (
                      <ActionBuyDownloadBeforeUpdate
                        purchasedDate={item?.downloadedAt || ''}
                        onDownload={handleDownloadContent}
                      />
                    ) : (
                      <ActionBuyDownload
                        onDownload={handleDownloadContent}
                      />
                    )}
                  </>
                ) : !item?.byVenue && item?.price ? (
                  <BuyContent
                    cost={item?.price || 0}
                    loading={isSaveLoading}
                    balance={availableBalance}
                    accountStatus={status}
                    onBuyContent={handleBuyContentDialog}
                    onUsageRight={handleUsageRightsDialog}
                  />
                ) : !item?.byVenue && !item?.price ? (
                  <NoPriceBuyContent />
                ) : null}
              </>
            )}
          </Box>
        </Stack>
      </Paper>
      <IconButton color='primary' onClick={() => handleNavigation('next')} sx={{ flexShrink: 0 }}>
        <ArrowForwardIcon />
      </IconButton>
    </Stack>
  );
};

export default Content;
